import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, map} from 'rxjs';
import {
  IHrDataOptions,
  IOneToOne,
  IPerformanceReview,
  IPromotion,
  IValuesHobbies,
} from 'src/app/interfaces/hr-data.interface';
import {IWorkingPeriod} from 'src/app/interfaces/termination.interface';
import {environment} from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HrDataService {
  private readonly url = `${environment.API}api/v1/employee/hrData`;

  constructor(private http: HttpClient) {}

  getHrDataOptions(employeeId: number): Observable<IHrDataOptions> {
    return this.http.get<IHrDataOptions>(`${this.url}/${employeeId}/options`);
  }

  getValuesAndHobbies(employeeId: number): Observable<IValuesHobbies> {
    return this.http.get<IValuesHobbies>(
      `${this.url}/${employeeId}/valuesAndHobbies`
    );
  }

  getOneToOnes(employeeId: number): Observable<IOneToOne[]> {
    return this.http.get<IOneToOne[]>(
      `${this.url}/${employeeId}/oneToOne/list`
    );
  }

  addOneToOne(data: IOneToOne): Observable<void> {
    return this.http.post<void>(`${this.url}/oneToOne`, data);
  }

  updateOneToOne(data: IOneToOne): Observable<void> {
    return this.http.put<void>(`${this.url}/oneToOne`, data);
  }

  deleteOneToOne(id: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/oneToOne/${id}`);
  }

  deleteTopic(topicId: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/oneToOne/topic/${topicId}`);
  }

  getPromotions(employeeId: number): Observable<IPromotion[]> {
    return this.http.get<IPromotion[]>(
      `${this.url}/${employeeId}/promotion/list`
    );
  }

  addPromotion(promotion: IPromotion): Observable<void> {
    return this.http.post<void>(`${this.url}/promotion`, promotion);
  }

  updatePromotion(promotion: IPromotion): Observable<void> {
    return this.http.put<void>(`${this.url}/promotion`, promotion);
  }

  deletePromotion(promotionId: number) {
    return this.http.delete(`${this.url}/promotion/${promotionId}`);
  }

  getPerformanceReviews(employeeId: number): Observable<IPerformanceReview[]> {
    return this.http.get<IPerformanceReview[]>(
      `${this.url}/${employeeId}/performanceReview/list`
    );
  }

  addPerformanceReview(
    performanceReview: IPerformanceReview
  ): Observable<void> {
    return this.http.post<void>(
      `${this.url}/performanceReview`,
      performanceReview
    );
  }

  updatePerformanceReview(
    performanceReview: IPerformanceReview
  ): Observable<void> {
    return this.http.put<void>(
      `${this.url}/performanceReview`,
      performanceReview
    );
  }

  deletePerformanceReview(promotionId: number) {
    return this.http.delete(`${this.url}/performanceReview/${promotionId}`);
  }

  deleteHrData(id: number, tableName: string): Observable<void> {
    return this.http.delete<void>(`${this.url}hrData/${tableName}/${id}`);
  }

  updateValuesAndHobbies(data: IValuesHobbies): Observable<IValuesHobbies> {
    return this.http
      .put<IValuesHobbies>(`${this.url}/valuesAndHobbies`, data)
      .pipe(map(() => data));
  }

  getWorkingPeriods(employeeId: number): Observable<IWorkingPeriod[]> {
    return this.http.get<IWorkingPeriod[]>(
      `${this.url}/${employeeId}/workingPeriod/list`
    );
  }
}
